import {Stack, useMergedRefs} from '@primer/react'
import {useCallback, useImperativeHandle, useRef} from 'react'
import {ConversationCommentBox, type ConversationCommentBoxHandle} from '@github-ui/diff-lines/conversation-comment-box'
import {
  type CreateTimelineCommentMutationVariables,
  useCreatePullRequestTimelineCommentMutation,
} from '../../mutations/use-create-pull-request-timeline-comment-mutation'
import {usePullRequestBasePath} from '../../stores/selectors'
import {useCopilotQuotaGate} from '../toolbar/use-copilot-quota-gate'
import {getCurrentUser} from './utils/get-author-filters'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {Link, useNavigate} from '@github-ui/react-router'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {CompactCommentButton} from '@github-ui/commenting/CompactCommentButton'
import {useLocalStorage} from '@github-ui/use-safe-storage/local-storage'
import {validateComment} from '@github-ui/entity-validators'
import {routeToIssueComment, useCurrentPathParams} from '../overview/data/use-comments-routing'
import {announce} from '@github-ui/aria-live'
import {CommentBoxButton} from '@github-ui/comment-box/CommentBox'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {ComposerFallback} from './ComposerFallback'
import {useReturnFocus} from '@github-ui/use-return-focus'
import type {CommentsDisplayMode} from './utils/display-mode'
import {isStringOrUndefined} from '../../utils/validators'
import {MutationFailureBanner} from '../FailureBanner'

export interface IssueCommentComposerHandle {
  /** Inserts quoted text into the draft, appending it to any existing draft when `append` is set. */
  insertQuote: (quotedText: string, options?: {append?: boolean}) => void
}

export type PullRequestCommentStateAction = 'close' | 'reopen'

export interface IssueCommentComposerStateActionProps {
  hasComment: boolean
  onSubmit: (stateAction: PullRequestCommentStateAction, onSuccess: () => void) => void
}

type OnSubmitCallback = (
  variables?: Omit<CreateTimelineCommentMutationVariables, 'text'>,
  callbacks?: {onSuccess?: () => void},
) => void

export interface ComposerAdditionalActionsContext {
  /** Is there currently a valid comment in the input? */
  isValid: boolean
  /** True if a submit comment request is pending, in which case we disable the buttons to prevent double submit. */
  isPending: boolean
  /** Call this method to submit the current comment, with optional additional variables. */
  submitComment: OnSubmitCallback
}

export interface IssueCommentComposerProps {
  /** Control if the composer is in collapsed state. Defaults to false. */
  collapsed?: boolean
  /** Called when collapsed should change. Leave undefined to disable collapsing functionality in the UI. */
  onCollapsedChange?: (collapsed: boolean) => void
  /** Called after a comment is successfully submitted. */
  onSubmitted?: () => void
  /**
   * Slot to render additional buttons below the composer. Can access `IssueCommentComposer` context to read state and
   * control the editor.
   */
  renderAdditionalActions?: (context: ComposerAdditionalActionsContext) => React.ReactNode
  ref?: React.Ref<IssueCommentComposerHandle>
}

function CollapsedCommentComposer({onClick, ref}: {onClick: () => void; ref?: React.Ref<HTMLButtonElement>}) {
  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const currentUser = getCurrentUser()

  return (
    <Stack gap="condensed" direction="horizontal" align="center">
      {currentUser && (
        <Link to={`/${currentUser.login}`} {...getHovercardAttributesForType('user', {login: currentUser.login})}>
          <GitHubAvatar size={32} src={currentUser.avatarUrl} alt="Your profile" />
        </Link>
      )}
      <CompactCommentButton onClick={onClick} ref={ref}>
        Add a comment
      </CompactCommentButton>
    </Stack>
  )
}

function AdditionalActions({
  render,
  context,
}: {
  render?: (context: ComposerAdditionalActionsContext) => React.ReactNode
  context: ComposerAdditionalActionsContext
}) {
  return render?.(context)
}

/**
 * Composer for a new pull request timeline (conversation) comment. Owns the draft state so it
 * can be dropped in anywhere conversation comments are rendered.
 */
function UnsafeIssueCommentComposer({
  collapsed = false,
  onCollapsedChange,
  ref,
  onSubmitted,
  renderAdditionalActions,
}: IssueCommentComposerProps) {
  const basePath = usePullRequestBasePath()
  const pathParams = useCurrentPathParams()
  const navigate = useNavigate()

  const [commentText = '', setCommentText, removePersistedCommentFromStorage] = useLocalStorage(
    `${basePath}-conversation-issue-new`,
    undefined,
    {validator: isStringOrUndefined},
  )

  const collapsible = onCollapsedChange !== undefined

  const createCommentMutation = useCreatePullRequestTimelineCommentMutation()

  // Handles return focus (to the collapsed editor) when collapsing the editor
  const collapsedEditorRef = useRef<HTMLButtonElement>(null)
  const {containerRef: editorContainerRef} = useReturnFocus(collapsedEditorRef)

  // Handles return focus (to the editor) when dismissing the error banner
  const commentBoxRef = useRef<ConversationCommentBoxHandle>(null)

  const editorAutofocusRef = useCallback(
    (editor: ConversationCommentBoxHandle | null) => {
      // Only autofocus when expanding collapsible editor, not on page load
      if (collapsible) editor?.focus()
    },
    [collapsible],
  )
  const mergedEditorRef = useMergedRefs(commentBoxRef, editorAutofocusRef)

  const formRef = useRef<HTMLFormElement>(null)
  const mergedFormRef = useMergedRefs(formRef, editorContainerRef)

  // The draft is retained while collapsed, but the quota gate should stay idle until it is
  // visible again — otherwise it would fetch quota data for a composer nobody can see.
  const {banner: quotaBanner, submitDisabled: quotaSubmitDisabled} = useCopilotQuotaGate(collapsed ? '' : commentText)

  const onClose = () => {
    onCollapsedChange?.(true)
    removePersistedCommentFromStorage()
  }

  const validationResult = validateComment(commentText)

  const disabled = createCommentMutation.isPending

  const onSubmit: OnSubmitCallback = (variables = {}, {onSuccess} = {}) => {
    if (quotaSubmitDisabled || disabled || !validationResult.isValid) return
    const submittedText = commentText

    // Starting the mutation will disable the editor which will lose focus position, so we shift focus to the container.
    // This case isn't handled by useReturnFocus since the editor isn't actually unmounted until it collapses.
    // We can remove this if we update MarkdownEditor to use aria-disabled internally.
    if (formRef.current?.contains(document.activeElement)) formRef.current?.focus()

    removePersistedCommentFromStorage()

    announce('Posting comment…')

    createCommentMutation.mutate(
      // A cast is required here only due to typescript limitations; discriminated union types spread about as easily as frozen butter
      {...variables, text: submittedText} as CreateTimelineCommentMutationVariables,
      {
        onSuccess: data => {
          onClose()
          onSubmitted?.()
          onSuccess?.()
          announce('New comment added successfully.')
          navigate(routeToIssueComment(pathParams, String(data.commentId)))
        },
        onError: () => {
          onCollapsedChange?.(false)
          setCommentText(submittedText)
          setTimeout(() => commentBoxRef.current?.focus())
        },
      },
    )
  }

  useImperativeHandle(
    ref,
    () => ({
      insertQuote(quotedText, {append = false} = {}) {
        onCollapsedChange?.(false)
        setCommentText((prev = '') => (append && prev.trim().length > 0 ? `${prev}\n\n${quotedText}` : quotedText))

        setTimeout(() => {
          commentBoxRef.current?.moveCursorToEnd()
        })
      },
    }),
    [onCollapsedChange, setCommentText],
  )

  const onChange = (value: string) => {
    if (!value) removePersistedCommentFromStorage()
    else setCommentText(value)
  }

  if (collapsed) return <CollapsedCommentComposer onClick={() => onCollapsedChange?.(false)} ref={collapsedEditorRef} />

  const additionalActionsContext: ComposerAdditionalActionsContext = {
    submitComment: onSubmit,
    isPending: createCommentMutation.isPending,
    isValid: validationResult.isValid,
  }

  return (
    <Stack
      as="form"
      onSubmit={onSubmit}
      ref={mergedFormRef}
      tabIndex={-1}
      aria-label="Add comment form"
      direction="vertical"
      gap="condensed"
    >
      {quotaBanner}

      <MutationFailureBanner
        mutation={createCommentMutation}
        title="Failed to add comment"
        returnFocusRef={commentBoxRef}
      />

      <ConversationCommentBox
        ref={mergedEditorRef}
        label="Add a comment"
        value={commentText}
        onChange={onChange}
        onSave={onSubmit}
        validationResult={validationResult}
        disabled={disabled}
        saveButtonText="Comment"
        maxHeightLines={15}
        saveButtonTrailingIcon={false}
        surface="description"
      >
        {collapsible && (
          <CommentBoxButton variant="default" onClick={onClose}>
            Cancel
          </CommentBoxButton>
        )}
        <AdditionalActions render={renderAdditionalActions} context={additionalActionsContext} />
      </ConversationCommentBox>
    </Stack>
  )
}

export function IssueCommentComposer(props: IssueCommentComposerProps) {
  return (
    <ErrorBoundary fallback={<ComposerFallback />} boundaryName="IssueCommentComposer">
      <UnsafeIssueCommentComposer {...props} />
    </ErrorBoundary>
  )
}

/**
 * Whether the conversation comment composer should be offered: only in conversation view,
 * and only when the current user is allowed to comment.
 */
export function isIssueCommentComposerEnabled(
  displayMode: CommentsDisplayMode,
  currentUserCanComment: boolean,
): boolean {
  return displayMode === 'conversation' && currentUserCanComment
}
